import { apiSlice } from "./apiSlice";
import { getUsers, setGroups, setUsersSelect, updateUserStatus } from "../slice/userSlice";
import { updatePerPageParams } from "../slice/authSlice";
import { paramsMap } from "../slice/utils";

/**
 * User API Slice
 *
 * RTK Query endpoints for user management operations including invitation
 * creation, user listing, pagination preferences, and user profile updates.
 */
export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    /**
     * Send a new invitation to a recipient email address.
     *
     * BUSINESS RULE: Invitations are the sole onboarding mechanism.
     * Throttled to 1 per 5 minutes per recipient email on the backend
     * to prevent spam.
     *
     * @endpoint POST /api_user/v2/invite/
     */
    addUser: builder.mutation({
      query: credentials => ({ body: credentials, method: "POST", url: "/api_user/v2/invite/" }),
      invalidatesTags: ["UserDirectory"],
    }),

    /**
     * Create a new items-per-page preference entry for the current user.
     * On success, updates the per-page params in the auth store and dispatches
     * the module-specific pagination action.
     *
     * @param {object} data
     * @param {object} [data.limit] - Module-keyed object, e.g. ``{ orders: 25 }``.
     */
    createItemsPerPage: builder.mutation({
      query: data => ({ url: "/user/items_per_page/", method: "POST", body: data }),
      onQueryStarted: ({ limit }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(updatePerPageParams(result.data));
            if (limit) {
              const [nameOfModule] = Object.keys(limit);
              dispatch(paramsMap[nameOfModule]({ limit: limit[nameOfModule] }));
            }
          })
          .catch(error => {});
      },
    }),

    /**
     * Fetch available permission groups (roles) for the invitation form.
     * Dispatches ``setGroups`` to populate the role dropdown.
     */
    getGroups: builder.query({
      query: () => "/user/group/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setGroups(result.data))).catch(error => {});
      },
    }),

    /**
     * Fetch the paginated list of users scoped to the requester's tenant.
     * Dispatches ``getUsers`` to hydrate the user management table.
     *
     * @param {string|null} params - URL query string for filtering/pagination.
     * @endpoint GET /api_user/v2/user/
     */
    getUsers: builder.query({
      query: (params = null) => (params ? `/api_user/v2/user/?${params}` : "/api_user/v2/user/"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getUsers(result.data))).catch(error => {});
      },
    }),

    /**
     * Unified Users directory: signed-up users + pending invitations.
     *
     * @param {string|null} params - URL query string for filtering/pagination.
     * @endpoint GET /api_user/v2/user/directory/
     */
    getUserDirectory: builder.query({
      query: (params = null) =>
        params ? `/api_user/v2/user/directory/?${params}` : "/api_user/v2/user/directory/",
      providesTags: ["UserDirectory"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getUsers(result.data))).catch(() => {});
      },
    }),

    /**
     * Fetch a lightweight list of users for collaboration dropdowns.
     * Dispatches ``setUsersSelect`` to populate selection components.
     *
     * @param {string} params - URL query string for filtering.
     * @endpoint GET /api_user/v2/user/
     */
    getUsersForCollab: builder.query({
      query: params => `/api_user/v2/user/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setUsersSelect(result.data))).catch(error => {});
      },
    }),

    /**
     * Update an existing items-per-page preference for a specific module.
     * On success, dispatches ``updatePerPageParams`` with the nested data.
     *
     * @param {object} params
     * @param {string} params.id - The preference record ID.
     * @param {object} params.data - The updated preference payload.
     */
    updateItemsPerPage: builder.mutation({
      query: ({ id, data }) => ({
        url: `/user/items_per_page/${id}/update_items_per_page/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updatePerPageParams(result.data.data)))
          .catch(error => {});
      },
    }),

    /**
     * Update a user's profile or status (PATCH).
     *
     * BUSINESS RULE: Self-updates allow all profile fields; admins updating
     * other users can only toggle ``is_active``. Members cannot update others.
     *
     * @param {object} params
     * @param {string} params.id - The user ID to update.
     * @param {object} params.rest - The fields to update.
     * @param {FormData} [params.formData] - Multipart body (e.g. ``profile_photo``); wins over rest.
     * @endpoint PATCH /api_user/v2/user/{id}/
     */
    updateUser: builder.mutation({
      query: ({ id, formData, ...rest }) => ({
        url: `/api_user/v2/user/${id}/`,
        method: "PATCH",
        body: formData || rest,
      }),
      invalidatesTags: ["UserDirectory"],
      onQueryStarted: ({ is_active }, { dispatch, queryFulfilled }) => {
        if (is_active === undefined) return;
        queryFulfilled
          .then(result => dispatch(updateUserStatus({ ...result.data, is_active })))
          .catch(() => {});
      },
    }),

    /**
     * Cancel a pending invitation (marks it Expired).
     *
     * @param {string|number} id - Invitation primary key (not the directory
     *   ``invitation:<pk>`` composite id).
     * @endpoint POST /api_user/v2/invite/{id}/cancel/
     */
    cancelInvitation: builder.mutation({
      query: id => ({
        url: `/api_user/v2/invite/${id}/cancel/`,
        method: "POST",
      }),
      invalidatesTags: ["UserDirectory"],
    }),
  }),
});

export const {
  useAddUserMutation,
  useCancelInvitationMutation,
  useCreateItemsPerPageMutation,
  useGetGroupsQuery,
  useGetUserDirectoryQuery,
  useGetUsersForCollabQuery,
  useGetUsersQuery,
  useUpdateItemsPerPageMutation,
  useUpdateUserMutation,
} = userApiSlice;
